import React, { useState } from 'react';
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    OutlinedInput,
    Checkbox,
    ListItemIcon,
    Chip,
    Button,
} from '@material-ui/core';
import { Block } from '@klp/design';
import { useTranslation } from 'react-i18next';
import classes from './StatusSelector.module.scss';

interface StatusSelectorProps {
    statusList?: Array<string>;
    statusListIsLoading?: boolean;
    value: Array<string>;
    onChange: (selected: Array<string>) => void;
    translationPrefix?: string; // e.g., 'prosjekt.statuses' or 'leverandor.statuses'
    // Translation key for a status, for statuses whose keys don't follow a prefix
    getStatusLabel?: (status: string) => string;
    label?: string;
    // 'chips' shows the selected statuses as chips, 'summary' shows "All" or "X selected"
    displayMode?: 'chips' | 'summary';
    // Translation key shown in summary mode when no or all statuses are selected
    allLabel?: string;
    disabled?: boolean;
}

/**
 * Generic status selector component.
 * Now a simple controlled component - state management handled by parent.
 */
export function StatusSelector({
    statusList,
    statusListIsLoading,
    value,
    onChange,
    translationPrefix,
    getStatusLabel,
    label = 'status.select',
    displayMode = 'chips',
    allLabel = 'status.all',
    disabled,
}: StatusSelectorProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [pendingSelection, setPendingSelection] =
        useState<Array<string>>(value);

    // Sync pending selection with value when it changes externally
    React.useEffect(() => {
        if (!open) {
            setPendingSelection(value);
        }
    }, [value, open]);

    if (!statusList || statusListIsLoading) {
        return <Block paddingBottom="tiny" />;
    }

    const isAllSelected =
        statusList.length > 0 && pendingSelection.length >= statusList.length;
    const isSummary = displayMode === 'summary';
    const statusLabelKey = (status: string) =>
        getStatusLabel
            ? getStatusLabel(status)
            : `${translationPrefix}.${status}`;

    const renderSummary = (selected: Array<string>) =>
        selected.length === 0 || selected.length >= statusList.length
            ? t(allLabel)
            : t('status.selectedCount', { antall: selected.length });

    const renderChips = (selected: Array<string>) => (
        <div className={classes.selectedValuesContainer}>
            {selected.slice(0, 3).map((val: string) => (
                <Chip
                    variant="outlined"
                    size="small"
                    key={val}
                    label={t(statusLabelKey(val))}
                />
            ))}
            {selected.length > 3 && (
                <Chip
                    variant="outlined"
                    size="small"
                    key="more"
                    label={`+${selected.length - 3}`}
                />
            )}
        </div>
    );

    const handleOpen = () => {
        setPendingSelection(value);
        setOpen(true);
    };

    const handleSelectionChange = (
        event: React.ChangeEvent<{ value: unknown }>,
    ) => {
        const selectedValues = event.target.value as Array<string>;
        const filteredValue = selectedValues.filter((s) => s);

        if (filteredValue[filteredValue.length - 1] === 'all') {
            const allDeselected = pendingSelection.length >= statusList.length;
            setPendingSelection(allDeselected ? [] : statusList);
            return;
        }

        setPendingSelection(filteredValue);
    };

    const handleApply = () => {
        onChange(pendingSelection);
        setOpen(false);
    };

    const handleClose = () => {
        // Apply changes on close
        handleApply();
    };

    return (
        <Block paddingBottom="tiny">
            <FormControl variant="outlined" disabled={disabled}>
                <InputLabel
                    id="status-selector-label"
                    shrink={isSummary ? true : undefined}
                >
                    {t(label)}
                </InputLabel>
                <Select
                    className={classes.selector}
                    labelId="status-selector-label"
                    id="status-selector"
                    multiple={true}
                    value={pendingSelection}
                    open={open}
                    onChange={handleSelectionChange}
                    onClose={handleClose}
                    onOpen={handleOpen}
                    // Summary mode always shows a value ("All"), so the label stays shrunk
                    displayEmpty={isSummary}
                    input={
                        <OutlinedInput
                            label={t(label)}
                            notched={isSummary ? true : undefined}
                        />
                    }
                    renderValue={(selected) =>
                        isSummary
                            ? renderSummary(selected as Array<string>)
                            : renderChips(selected as Array<string>)
                    }
                >
                    <Block
                        paddingLeft="compact"
                        paddingTop="compact"
                        paddingBottom="compact"
                        className={classes.sticky}
                    >
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleApply}
                        >
                            {t('status.filter')}
                        </Button>
                    </Block>
                    <MenuItem value="all">
                        <ListItemIcon>
                            <Checkbox
                                checked={isAllSelected}
                                indeterminate={
                                    pendingSelection.length > 0 &&
                                    pendingSelection.length < statusList.length
                                }
                            />
                        </ListItemIcon>
                        {t('status.all')}
                    </MenuItem>
                    {/* eslint-disable-next-line i18next/no-literal-string */}
                    <MenuItem key="divider" value="divider" disabled={true}>
                        - - - - - - - - - -
                    </MenuItem>
                    {statusList.map((name) => (
                        <MenuItem key={name} value={name}>
                            <ListItemIcon>
                                <Checkbox
                                    checked={
                                        pendingSelection.indexOf(name) > -1
                                    }
                                />
                            </ListItemIcon>
                            {t(statusLabelKey(name))}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Block>
    );
}
